import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import Icon from '@mdi/react';
import {
    mdiAccount,
    mdiAccountBadgeOutline,
    mdiAccountGroup,
    mdiCheckCircle,
    mdiDomain,
    mdiFileDocumentOutline,
    mdiMedicalBag,
    mdiOfficeBuilding,
    mdiOpenInNew,
    mdiTune,
    mdiWalletOutline,
    mdiAlertCircleOutline,
    mdiArrowDown,
    mdiNoteEditOutline,
} from '@mdi/js';
import { ClaimEditorStore } from '../../stores/claim-editor';
import {
    ClaimSectionIcon,
    ClaimSectionId,
    CLAIM_EDITOR_SECTIONS,
    scrollToField,
    scrollToSection,
    sectionDomId,
} from '../../stores/claim-editor/claim-editor-sections';
import {
    flattenClaimValidationErrors,
    formIssueRemainder,
    resolveClaimValidationJump,
} from '../../stores/claim-editor/claim-validation-errors';
import { Spinner } from '../base/spinner';
import * as S from './claim-editor.styles';
import { Variables } from '../base/variables';

const SECTION_ICONS: Record<ClaimSectionIcon, string> = {
    person: mdiAccount,
    wallet: mdiWalletOutline,
    badge: mdiAccountBadgeOutline,
    medical: mdiMedicalBag,
    domain: mdiDomain,
    apartment: mdiOfficeBuilding,
    groups: mdiAccountGroup,
    description: mdiFileDocumentOutline,
    tune: mdiTune,
};

interface ClaimEditorNavProps {
    claimEditorStore: ClaimEditorStore;
    disabledSections?: string[];
    claimId?: string;
}

export const ClaimEditorNav: React.FunctionComponent<ClaimEditorNavProps> = observer(
    ({ claimEditorStore, disabledSections = [], claimId }) => {
        const { accountId } = useParams<{ accountId?: string }>();
        const [activeSection, setActiveSection] = useState<ClaimSectionId>('patient');
        // A clicked section can land with a still-larger neighbor sharing the screen (e.g. the
        // anchored jump to Additional Info/Settings deliberately leaves Additional Providers
        // mostly visible) — the observer below would otherwise re-highlight that neighbor the
        // instant the scroll settles. Pin the clicked section until intersection data agrees
        // with it or this cools down.
        const pinnedSectionRef = useRef<{ id: ClaimSectionId; until: number } | null>(null);
        const statuses = claimEditorStore.sectionStatuses.filter(
            s => !s.disabledKey || !disabledSections.includes(s.disabledKey),
        );
        const showClaimLink = Boolean(claimId) && !claimEditorStore.isCreateClaim;
        const claimLabel = claimEditorStore.claimDetails?.claim?.accountNumber || claimId;
        const detailsHref = claimId
            ? accountId
                ? `/${accountId}/claims/${claimId}`
                : `/claims/${claimId}`
            : '';

        useEffect(() => {
            const observers: IntersectionObserver[] = [];
            const visible = new Map<ClaimSectionId, number>();

            statuses.forEach(section => {
                const el = document.getElementById(sectionDomId(section.id));
                if (!el) return;
                const observer = new IntersectionObserver(
                    entries => {
                        entries.forEach(entry => {
                            visible.set(section.id, entry.intersectionRatio);
                        });
                        let best: ClaimSectionId | null = null;
                        let bestRatio = 0;
                        visible.forEach((ratio, id) => {
                            if (ratio > bestRatio) {
                                bestRatio = ratio;
                                best = id;
                            }
                        });
                        const pinned = pinnedSectionRef.current;
                        if (pinned) {
                            if (Date.now() < pinned.until && best !== pinned.id) return;
                            pinnedSectionRef.current = null;
                        }
                        if (best) setActiveSection(best);
                    },
                    { rootMargin: '-20% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] },
                );
                observer.observe(el);
                observers.push(observer);
            });

            return () => observers.forEach(o => o.disconnect());
        }, [statuses.map(s => s.id).join(',')]);

        return (
            <S.NavRail>
                {showClaimLink && detailsHref && claimLabel && (
                    <S.ClaimLinkBlock>
                        <S.ClaimLinkLabel>Editing claim</S.ClaimLinkLabel>
                        <S.ClaimDetailsLink
                            to={detailsHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`View claim ${claimLabel}`}>
                            {claimLabel}
                            <S.ClaimDetailsLinkIcon path={mdiOpenInNew} size="16px" />
                        </S.ClaimDetailsLink>
                    </S.ClaimLinkBlock>
                )}
                <S.NavTitle>Sections</S.NavTitle>
                {statuses.map(section => {
                    const active = activeSection === section.id;
                    return (
                        <S.NavItem
                            key={section.id}
                            variant="unstyled"
                            type="button"
                            $active={active}
                            onClick={() => {
                                setActiveSection(section.id);
                                pinnedSectionRef.current = { id: section.id, until: Date.now() + 1000 };
                                scrollToSection(section.id);
                            }}>
                            <S.NavIcon>
                                <Icon path={SECTION_ICONS[section.icon]} size="18px" />
                            </S.NavIcon>
                            <S.NavItemLabel>{section.navLabel}</S.NavItemLabel>
                            {section.errorCount > 0 && claimEditorStore.validationEvaluated ? (
                                <S.NavBadge>{section.errorCount}</S.NavBadge>
                            ) : section.done ? (
                                <S.NavDone>
                                    <Icon path={mdiCheckCircle} size="17px" />
                                </S.NavDone>
                            ) : null}
                        </S.NavItem>
                    );
                })}
            </S.NavRail>
        );
    },
);

interface ClaimErrorSummaryBannerProps {
    claimEditorStore: ClaimEditorStore;
    dismissed: boolean;
    onDismiss: () => void;
    claimValidationErrors?: unknown;
}

const ServerValidationMessages: React.FunctionComponent<{ messages: string[]; alongsideFormIssues?: boolean }> = ({
    messages,
    alongsideFormIssues = false,
}) => (
    <S.ErrorSummaryGroup>
        <S.ErrorSummarySourceTitle>System error messages</S.ErrorSummarySourceTitle>
        <S.ErrorSummaryHint>
            {alongsideFormIssues
                ? 'Returned by the server when this claim was last validated. Wording can match a form issue — they are a separate check.'
                : 'Returned by the server when this claim was last validated.'}
        </S.ErrorSummaryHint>
        {messages.map(message => {
            const sectionId = resolveClaimValidationJump(message);
            if (sectionId) {
                return (
                    <S.ErrorSummaryLink
                        key={message}
                        variant="unstyled"
                        type="button"
                        onClick={() => scrollToSection(sectionId)}>
                        → {message}
                    </S.ErrorSummaryLink>
                );
            }
            return <S.ErrorSummaryPlain key={message}>{message}</S.ErrorSummaryPlain>;
        })}
    </S.ErrorSummaryGroup>
);

export const ClaimErrorSummaryBanner: React.FunctionComponent<ClaimErrorSummaryBannerProps> = observer(
    ({ claimEditorStore, dismissed, onDismiss, claimValidationErrors }) => {
        const [serverExpanded, setServerExpanded] = useState(false);
        if (dismissed) return null;

        const fieldErrors = claimEditorStore.validationEvaluated ? claimEditorStore.fieldErrors : [];
        const serverMessages = flattenClaimValidationErrors(claimValidationErrors);
        if (!fieldErrors.length && !serverMessages.length) return null;

        const hasFieldErrors = fieldErrors.length > 0;
        const bySection = claimEditorStore.errorsBySection;
        const groups = CLAIM_EDITOR_SECTIONS.map(section => ({
            title: section.title,
            id: section.id,
            items: bySection[section.id] || [],
        })).filter(g => g.items.length > 0);

        const titleCount = hasFieldErrors ? fieldErrors.length : serverMessages.length;
        const title = hasFieldErrors
            ? titleCount === 1
                ? '1 issue to fix before submitting'
                : `${titleCount} issues to fix before submitting`
            : titleCount === 1
              ? '1 system error message'
              : `${titleCount} system error messages`;

        const systemToggleLabel = serverExpanded
            ? 'Hide system error messages'
            : `Show system error messages (${serverMessages.length})`;

        return (
            <S.ErrorSummaryBanner id="claim-error-banner">
                <S.ErrorSummaryHeader>
                    <Icon path={mdiAlertCircleOutline} size="18px" color={Variables.ThemeLight.WarningConstant6} />
                    <S.ErrorSummaryTitle>{title}</S.ErrorSummaryTitle>
                    <S.ErrorSummaryDismiss variant="unstyled" type="button" onClick={onDismiss} aria-label="Dismiss">
                        ×
                    </S.ErrorSummaryDismiss>
                </S.ErrorSummaryHeader>
                {hasFieldErrors && (
                    <S.ErrorSummaryHint>Jump to any field to fix it. The list updates as you go.</S.ErrorSummaryHint>
                )}
                <S.ErrorSummaryGroups>
                    {hasFieldErrors && (
                        <S.ErrorSummaryGroup>
                            <S.ErrorSummarySourceTitle>Form issues</S.ErrorSummarySourceTitle>
                            <S.ErrorSummaryHint>Checked in this form before submit.</S.ErrorSummaryHint>
                        </S.ErrorSummaryGroup>
                    )}
                    {hasFieldErrors &&
                        groups.map(group => (
                            <S.ErrorSummaryGroup key={group.id}>
                                <S.ErrorSummaryGroupTitle>{group.title}</S.ErrorSummaryGroupTitle>
                                {group.items.map(item => {
                                    const remainder = formIssueRemainder(item.label, item.message);
                                    return (
                                        <S.ErrorSummaryLink
                                            key={item.field}
                                            variant="unstyled"
                                            type="button"
                                            onClick={() => scrollToField(item.field, item.sectionId)}>
                                            →
                                            <S.ErrorSummaryPlace>{item.label}</S.ErrorSummaryPlace>
                                            {remainder ? (
                                                <S.ErrorSummaryRest>{remainder}</S.ErrorSummaryRest>
                                            ) : null}
                                        </S.ErrorSummaryLink>
                                    );
                                })}
                            </S.ErrorSummaryGroup>
                        ))}
                    {serverMessages.length > 0 && !hasFieldErrors && (
                        <ServerValidationMessages messages={serverMessages} />
                    )}
                    {serverMessages.length > 0 && hasFieldErrors && serverExpanded && (
                        <ServerValidationMessages messages={serverMessages} alongsideFormIssues />
                    )}
                </S.ErrorSummaryGroups>
                {serverMessages.length > 0 && hasFieldErrors && (
                    <S.ErrorSummaryToggle
                        variant="text"
                        type="button"
                        onClick={() => setServerExpanded(open => !open)}>
                        {systemToggleLabel}
                    </S.ErrorSummaryToggle>
                )}
            </S.ErrorSummaryBanner>
        );
    },
);

interface ClaimEditorStickyFooterProps {
    claimEditorStore: ClaimEditorStore;
    title: string;
    submitLabel: string;
    isSubmitting?: boolean;
    onSubmit: () => void;
    onCancel?: () => void;
    headerActions?: React.ReactNode;
}

export const ClaimEditorStickyFooter: React.FunctionComponent<ClaimEditorStickyFooterProps> = observer(
    ({ claimEditorStore, title, submitLabel, isSubmitting, onSubmit, onCancel, headerActions }) => {
        const issueIdxRef = useRef(-1);
        const errors = claimEditorStore.fieldErrors;
        const evaluated = claimEditorStore.validationEvaluated;
        const hasErrors = evaluated && errors.length > 0;

        const jumpToNextIssue = () => {
            if (!errors.length) return;
            const next = (issueIdxRef.current + 1) % errors.length;
            issueIdxRef.current = next;
            const err = errors[next];
            scrollToField(err.field, err.sectionId);
        };

        const footerText = !evaluated
            ? 'Fill in required fields to create the claim'
            : hasErrors
              ? `${errors.length} ${errors.length === 1 ? 'issue remaining' : 'issues remaining'}`
              : 'All checks passed';

        return (
            <S.StickyFooter>
                <S.StickyFooterLeft>
                    <S.StickyFooterTitle>
                        {title}
                        {headerActions}
                    </S.StickyFooterTitle>
                    <S.StickyFooterStatus $hasErrors={hasErrors} $ok={evaluated && !hasErrors}>
                        <Icon
                            path={
                                hasErrors
                                    ? mdiAlertCircleOutline
                                    : evaluated
                                      ? mdiCheckCircle
                                      : mdiNoteEditOutline
                            }
                            size="18px"
                        />
                        {footerText}
                    </S.StickyFooterStatus>
                    {hasErrors && (
                        <S.JumpNextButton variant="unstyled" type="button" onClick={jumpToNextIssue}>
                            <Icon path={mdiArrowDown} size="17px" />
                            Jump to next issue
                        </S.JumpNextButton>
                    )}
                </S.StickyFooterLeft>
                <S.StickyFooterActions>
                    {onCancel && (
                        <S.StickyFooterCancelButton
                            variant="outlined"
                            onClick={onCancel}
                            disabled={isSubmitting}>
                            Cancel
                        </S.StickyFooterCancelButton>
                    )}
                    <S.StickyFooterSubmitButton onClick={onSubmit} disabled={isSubmitting}>
                        {isSubmitting ? <Spinner size="20px" /> : submitLabel}
                    </S.StickyFooterSubmitButton>
                </S.StickyFooterActions>
            </S.StickyFooter>
        );
    },
);

interface ClaimSuccessPanelProps {
    claimId: string;
    onViewClaim: () => void;
    onCreateAnother: () => void;
}

export const ClaimSuccessPanel: React.FunctionComponent<ClaimSuccessPanelProps> = ({
    claimId,
    onViewClaim,
    onCreateAnother,
}) => {
    return (
        <S.SuccessPanel>
            <S.SuccessIcon>✓</S.SuccessIcon>
            <S.SuccessTitle>Claim created</S.SuccessTitle>
            <S.SuccessSubtitle>
                Your claim <strong>{claimId}</strong> was created successfully.
            </S.SuccessSubtitle>
            <S.SuccessActions>
                <S.SuccessSecondaryButton type="button" onClick={onCreateAnother}>
                    Create Another
                </S.SuccessSecondaryButton>
                <S.SuccessPrimaryButton type="button" onClick={onViewClaim}>
                    View Claim
                </S.SuccessPrimaryButton>
            </S.SuccessActions>
        </S.SuccessPanel>
    );
};

interface SectionStatusPillProps {
    sectionId: ClaimSectionId;
    claimEditorStore: ClaimEditorStore;
}

export const SectionStatusPill: React.FunctionComponent<SectionStatusPillProps> = observer(
    ({ sectionId, claimEditorStore }) => {
        if (!claimEditorStore.validationEvaluated) return null;
        const status = claimEditorStore.sectionStatuses.find(s => s.id === sectionId);
        if (!status) return null;
        if (status.errorCount > 0) {
            return (
                <S.StatusPill $tone="error">
                    {status.errorCount} {status.errorCount === 1 ? 'issue' : 'issues'}
                </S.StatusPill>
            );
        }
        if (status.done) {
            return <S.StatusPill $tone="success">Complete</S.StatusPill>;
        }
        return null;
    },
);
